import type { Database } from 'sql.js';
import { lookupBonoNombreById } from '@core/infrastructure/driven/sqljs/sqlJsLookupBonoNombre.ts';
import {
  captureTipoBonoTextFromSqlRow,
  lookupTipoBonoNombreById,
  participanteTipoBonoCatalogId,
} from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';
import {
  publicFileUrl,
  queryOne,
  rowFirstKeyColumn as pepCol,
  sqlPositiveIntId,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { lookupParticipanteSqlRowByDocumentoOrNombre } from '../../../../shared/participante/infrastructure/findParticipanteSqlByDocumentoOrNombre.ts';
import { listParticipanteSqlRowsByDocumentoOrNombre } from '../../../../shared/participante/infrastructure/findParticipanteSqlByDocumentoOrNombre.ts';
import { mapParticipanteSqlRowToBusquedaCandidate } from '../../../../shared/participante/infrastructure/mapParticipanteSqlRowToBusquedaCandidate.ts';
import type { ParticipanteBusquedaCandidate } from '../../../../shared/participante/domain/participanteBusquedaCandidate.ts';
import type { ParticipanteBusquedaPrefillResolve } from '../../../../shared/participante/domain/participanteBusquedaPrefillResolve.ts';
import type {
  ParticipantePuntosEntregaBusquedaPrefill,
  ParticipanteResumen,
} from '../../../domain/puntoEntregaModel.ts';
import { resolveAcudienteRow } from './acudienteResolve.ts';
import { participanteRowToResumen } from './mappers.ts';

const PARTICIPANT_LOOKUP_SQL = `SELECT p.*, c.nombre AS comuna_nombre
  FROM participantes p
  LEFT JOIN comunas c ON c.id = p.comuna_id`;

/** Participant lookup helpers used by `SqlJsPuntosEntregaRepository`. */

export function findParticipanteByDocumento(db: Database, documento: string): ParticipanteResumen | null {
  const lookup = lookupParticipanteSqlRowByDocumentoOrNombre(db, documento);
  if (lookup.status !== 'found') return null;
  return participanteRowToResumen(lookup.row);
}

export function findParticipanteById(db: Database, id: number): ParticipanteResumen | null {
  const p = queryOne(db, `${PARTICIPANT_LOOKUP_SQL} WHERE p.id = ? AND p.estado = 1`, [id]);
  if (!p) return null;
  return participanteRowToResumen(p);
}

/** Master-sync catalog id on `participantes` (used for bono auto-create hint, not PEP capture). */
export { participanteTipoBonoCatalogId } from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';

function defaultTipoBonoTextForParticipante(db: Database, participanteId: number): string {
  return lookupTipoBonoNombreById(db, participanteTipoBonoCatalogId(db, participanteId)) ?? '';
}

function buildPuntosEntregaBusquedaPrefillFromResumen(
  db: Database,
  filesPublicPrefix: string,
  resumen: ParticipanteResumen,
): ParticipantePuntosEntregaBusquedaPrefill {
  const catalogTipoBono = defaultTipoBonoTextForParticipante(db, resumen.id);
  const pep = queryOne(
    db,
    `SELECT * FROM punto_entrega_participante WHERE participante_id = ? ORDER BY id DESC LIMIT 1`,
    [resumen.id],
  );
  if (!pep) {
    return {
      resumen,
      desdeUltimoPuntoEntrega: false,
      nombreParticipante: resumen.nombre,
      telefono: resumen.telefono,
      celular: resumen.celular,
      personaAutorizada: false,
      nombrePersonaAutorizada: '',
      asistencia: false,
      acta: '',
      entregaBolsa: false,
      bonoId: null,
      bonoNombre: null,
      tipoBono: catalogTipoBono,
      firmaBase64: '',
      observaciones: '',
      registroFotografico: '',
      politicaTratamientos: resumen.aceptaTerminos,
    };
  }
  const esAcud = Number(pepCol(pep, 'es_acudiente', 'ES_ACUDIENTE') ?? 0) === 1;
  let nombrePersonaAutorizada = '';
  if (esAcud) {
    const acudienteRow = resolveAcudienteRow(db, pep);
    if (acudienteRow) {
      nombrePersonaAutorizada = String(acudienteRow.nombre ?? '').trim();
    }
  }
  const pepTipoBono = captureTipoBonoTextFromSqlRow(db, pep);
  const tipoBono = pepTipoBono.length > 0 ? pepTipoBono : defaultTipoBonoTextForParticipante(db, resumen.id);
  const bonoId = sqlPositiveIntId(pepCol(pep, 'bono_id', 'BONO_ID'));

  return {
    resumen,
    desdeUltimoPuntoEntrega: true,
    nombreParticipante: resumen.nombre,
    telefono: resumen.telefono,
    celular: resumen.celular,
    personaAutorizada: esAcud,
    nombrePersonaAutorizada,
    asistencia: Number(pepCol(pep, 'asistencia', 'ASISTENCIA') ?? 0) === 1,
    acta: publicFileUrl(pepCol(pep, 'acta', 'ACTA') ?? '', filesPublicPrefix),
    entregaBolsa: Number(pepCol(pep, 'entrega_bolsa', 'ENTREGA_BOLSA') ?? 0) === 1,
    bonoId,
    bonoNombre: lookupBonoNombreById(db, bonoId),
    tipoBono,
    firmaBase64: publicFileUrl(pepCol(pep, 'firma_base64', 'FIRMA_BASE64') ?? '', filesPublicPrefix),
    observaciones: String(pepCol(pep, 'observaciones', 'OBSERVACIONES') ?? ''),
    registroFotografico: publicFileUrl(
      pepCol(pep, 'registro_fotografico', 'REGISTRO_FOTOGRAFICO') ?? '',
      filesPublicPrefix,
    ),
    politicaTratamientos: Number(pepCol(pep, 'politica_tratamientos', 'POLITICA_TRATAMIENTOS') ?? 0) === 1,
  };
}

export function resolveParticipantePuntosEntregaBusquedaPrefill(
  db: Database,
  filesPublicPrefix: string,
  documento: string,
): ParticipanteBusquedaPrefillResolve<ParticipantePuntosEntregaBusquedaPrefill> {
  const lookup = lookupParticipanteSqlRowByDocumentoOrNombre(db, documento);
  if (lookup.status === 'found') {
    return {
      ok: true,
      prefill: buildPuntosEntregaBusquedaPrefillFromResumen(
        db,
        filesPublicPrefix,
        participanteRowToResumen(lookup.row),
      ),
    };
  }
  return { ok: false, reason: lookup.status === 'empty' ? 'empty' : lookup.status };
}

export function resolveParticipantePuntosEntregaBusquedaPrefillByParticipanteId(
  db: Database,
  filesPublicPrefix: string,
  participanteId: number,
): ParticipanteBusquedaPrefillResolve<ParticipantePuntosEntregaBusquedaPrefill> {
  const resumen = findParticipanteById(db, participanteId);
  if (!resumen) return { ok: false, reason: 'not_found' };
  return {
    ok: true,
    prefill: buildPuntosEntregaBusquedaPrefillFromResumen(db, filesPublicPrefix, resumen),
  };
}

export function listParticipantePuntosEntregaBusquedaCandidates(
  db: Database,
  term: string,
): ParticipanteBusquedaCandidate[] {
  return listParticipanteSqlRowsByDocumentoOrNombre(db, term).map(mapParticipanteSqlRowToBusquedaCandidate);
}

export function findParticipantePuntosEntregaBusquedaPrefillPorDocumento(
  db: Database,
  filesPublicPrefix: string,
  documento: string,
): ParticipantePuntosEntregaBusquedaPrefill | null {
  const resolved = resolveParticipantePuntosEntregaBusquedaPrefill(db, filesPublicPrefix, documento);
  return resolved.ok ? resolved.prefill : null;
}

export function findPuntoEntregaIdForParticipante(db: Database, participanteId: number): number | null {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return null;
  const row = queryOne(
    db,
    'SELECT punto_entrega_id AS pid FROM punto_entrega_participante WHERE participante_id = ? LIMIT 1',
    [participanteId],
  );
  return sqlPositiveIntId(row?.pid);
}

/**
 * Another delivery point id for the same catalog entrega when the participant would duplicate entrega, or null.
 */
export function findConflictingPuntoEntregaIdForParticipante(
  db: Database,
  participanteId: number,
  entregaCatalogId: number,
  excludedPuntoEntregaId: number | null,
): number | null {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return null;
  if (!Number.isFinite(entregaCatalogId) || entregaCatalogId <= 0) return null;
  const row = queryOne(
    db,
    `SELECT pep.punto_entrega_id AS pid
     FROM punto_entrega_participante pep
     INNER JOIN punto_entrega pe ON pep.punto_entrega_id = pe.id
     WHERE pep.participante_id = ? AND pe.entrega_id = ?
       AND (? IS NULL OR pep.punto_entrega_id != ?)
     LIMIT 1`,
    [participanteId, entregaCatalogId, excludedPuntoEntregaId, excludedPuntoEntregaId],
  );
  return sqlPositiveIntId(row?.pid);
}
